import { Alert, Box, CircularProgress, Container, Divider, Typography, Tabs, Tab, TextField, Button } from "@mui/material";
import { AdminPanelSettings, Person, Lock, Email } from "@mui/icons-material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { useGetAdminUserQuery } from "features/adminUsers/adminUsers";
import { useAppSelector } from "features/store";

export default function ClientViewAccountSettingsPage() {
  const { userId } = useParams<{ userId: string }>();
  const id = userId ? Number(userId) : NaN;
  const clientView = useAppSelector((state) => state.clientView);
  const [activeSection, setActiveSection] = useState<"profile" | "email" | "password">("profile");

  const { data: user, isLoading, error } = useGetAdminUserQuery(id, {
    skip: !id || Number.isNaN(id),
  });

  if (!clientView.active || !clientView.userId || clientView.userId !== id) {
    return (
      <Container component="main" maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="info">Client View is not active for this user.</Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Container component="main" maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Failed to load client profile.</Alert>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: "rgb(31, 93, 107)" }}>
        Account Settings (Client View)
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 4,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box sx={{ minWidth: 200 }}>
          <Tabs
            orientation="vertical"
            value={activeSection}
            onChange={(_, v) => setActiveSection(v)}
            sx={{
              "& .MuiTab-root": {
                alignItems: "flex-start",
                textTransform: "none",
                fontWeight: 600,
              },
            }}
          >
            <Tab value="profile" label="Profile" />
            <Tab value="email" label="Email" />
            <Tab value="password" label="Password" />
          </Tabs>
        </Box>

        <Box
          sx={{
            flex: 1,
            bgcolor: "background.paper",
            borderRadius: 3,
            p: 3,
            boxShadow: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This is a read-only view of the client's account. All fields are disabled in Client View.
          </Typography>
          {activeSection === "profile" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 3 }}>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    bgcolor: "rgb(31, 93, 107)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Person sx={{ fontSize: 40, color: "#fff" }} />
                </Box>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography variant="h6">
                      {user.first_name || user.last_name ? `${user.first_name} ${user.last_name}`.trim() : user.username}
                    </Typography>
                    {user.profile?.is_superuser && (
                      <AdminPanelSettings sx={{ fontSize: 20, color: "primary.main" }} titleAccess="Super Admin" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    @{user.username}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Username"
                  value={user.username}
                  size="small"
                  fullWidth
                  disabled
                />
                <TextField
                  label="Client Name"
                  value={user.first_name || ""}
                  size="small"
                  fullWidth
                  disabled
                />
                <TextField
                  label="Email"
                  value={user.email || ""}
                  size="small"
                  fullWidth
                  disabled
                />
              </Box>
            </>
          )}

          {activeSection === "email" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Email sx={{ color: "primary.main" }} />
                <Typography variant="h6">Email</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This is how the client can change their email. In Client View fields are disabled.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
                <TextField label="Current Email" value={user.email || ""} size="small" fullWidth disabled />
                <TextField label="New Email" value="" size="small" fullWidth disabled />
                <Button variant="contained" disabled sx={{ textTransform: "none", fontWeight: 700 }}>
                  Update Email
                </Button>
              </Box>
            </>
          )}

          {activeSection === "password" && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Lock sx={{ color: "primary.main" }} />
                <Typography variant="h6">Password</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This is how the client can change their password. In Client View all controls are disabled.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
                <TextField label="Current Password" type="password" value="" size="small" fullWidth disabled />
                <TextField label="New Password" type="password" value="" size="small" fullWidth disabled />
                <TextField label="Confirm New Password" type="password" value="" size="small" fullWidth disabled />
                <Button variant="contained" disabled sx={{ textTransform: "none", fontWeight: 700 }}>
                  Update Password
                </Button>
              </Box>
            </>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            This page mirrors what the client sees for their account settings. Changes cannot be made from Client View.
          </Alert>
        </Box>
      </Box>
    </Container>
  );
}

